import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge, Box, IconButton, Popover, Tooltip, Typography, Divider,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import * as C from '../../theme/colors.constants';
import { rota } from '../../prototipo.config';
import {
  findQuantidadeRequisicoesAction,
  findQuantidadeAutorizacoesAction,
  findQuantidadeAcordosAprovadosAction,
} from '../../../store/theme.actions';
import { lerNotificacaoProcessoAtivoService } from '../../../services/core/processos/processosRequisicoesService';
import { setOpenDialogAlterarSenhaAction } from '../../../store/Login/login.store';
import { logoutAction } from '../../../store/Login/login.saga';
import useDispararPesquisaAtalho from '../../hooks/useDispararPesquisaAtalho';
import AlterarSenhaDialog from './AlterarSenhaDialog';

// Rota raiz do dashboard real, fora do módulo de protótipo.
const ROTA_DASHBOARD = '/';

/**
 * Barra superior.
 *
 * Reproduz `abe-web-react/src/components/Toolbar/Toolbar.jsx`, mas com duas
 * mudanças vindas dos prints:
 *   1. É transparente sobre o fundo `#e9edf2` (no projeto real tem gradiente cinza).
 *   2. Os ícones são outline em navy, na ordem: home, balão, cifrão, sino,
 *      engrenagem, sair.
 *
 * O comportamento de notificação foi mantido igual ao real: `Badge` com o total
 * e um popover ancorado sob o ícone, que só abre quando há itens. Os dados vêm
 * do mesmo estado Redux `theme` já populado por `store/menu.saga.js` — nunca
 * disparar `initializePageAction()` a partir daqui, ele também sobrescreve
 * `state.theme.title`, usado pelo `Toolbar` real, e vazaria o título da tela
 * real para dentro do protótipo (ver docs/architecture/plano-remocao-mocks-home.md,
 * Etapa 7).
 */
function NotificacaoIcon({
  tipo, icone, dados, onNavigate,
}) {
  const [anchor, setAnchor] = useState(null);
  const aberto = Boolean(anchor);

  return (
    <>
      <Tooltip title={dados.hint} arrow>
        <IconButton
          aria-label={dados.hint}
          onClick={e => dados.total > 0 && setAnchor(e.currentTarget)}
          sx={{ color: C.navy }}
        >
          <Badge
            badgeContent={dados.total}
            color="error"
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
          >
            {icone}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={aberto}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            borderRadius: 2,
            boxShadow: C.shadowFloat,
            border: `1px solid ${C.borderGrey}`,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="caption" sx={{ color: C.warmGrey, fontWeight: 700, letterSpacing: 0.4 }}>
            {dados.hint.toUpperCase()}
          </Typography>
        </Box>
        <Divider />
        {dados.itens.map(item => (
          <Box
            key={item.key}
            onClick={() => {
              setAnchor(null);
              if (onNavigate) onNavigate(tipo, item);
            }}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              '&:hover': { backgroundColor: C.paleGrey },
            }}
          >
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%', backgroundColor: C.navy, mt: 0.75, flexShrink: 0,
            }}
            />
            <Typography variant="body2" sx={{ color: C.black }}>{item.texto}</Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
}
NotificacaoIcon.propTypes = {
  tipo: PropTypes.string.isRequired,
  icone: PropTypes.node.isRequired,
  dados: PropTypes.shape({
    hint: PropTypes.string,
    total: PropTypes.number,
    itens: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      texto: PropTypes.string,
      idProcesso: PropTypes.number,
    })),
  }).isRequired,
  onNavigate: PropTypes.func,
};
NotificacaoIcon.defaultProps = { onNavigate: null };

export default function Topbar({ titulo }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { disparar, snackbar } = useDispararPesquisaAtalho();

  const quantidadeNotificacoes = useSelector(state => state.theme.quantidadeNotificacoes) || 0;
  const quantidadeAutorizacoes = useSelector(state => state.theme.quantidadeAutorizacoes) || 0;
  const dadosAcordosAprovados = useSelector(state => state.theme.quantidadeAcordosAprovados) || {};
  const quantidadeAcordosAprovados = dadosAcordosAprovados.qtdeProcessosAtivos || 0;
  const idsProcessoAcordosAprovados = dadosAcordosAprovados.idsProcessoAtivos || [];
  const nrosAcordo = dadosAcordosAprovados.nrosAcordo || [];

  useEffect(() => {
    dispatch(findQuantidadeRequisicoesAction());
    dispatch(findQuantidadeAutorizacoesAction());
    dispatch(findQuantidadeAcordosAprovadosAction());
  }, [dispatch]);

  const dadosBalao = {
    hint: 'Propostas avaliadas',
    total: quantidadeAcordosAprovados,
    itens: idsProcessoAcordosAprovados.map((idProcesso, index) => ({
      key: `${idProcesso}-${index}`,
      idProcesso,
      texto: `A proposta nº ${nrosAcordo[index] || ''} do Processo nº ${idProcesso} foi avaliada.`,
    })),
  };

  const dadosCifrao = {
    hint: 'Propostas aguardando aprovação',
    total: quantidadeAutorizacoes,
    itens: [{ key: 'autorizacao', texto: 'Você tem proposta aguardando aprovação.' }],
  };

  const dadosSino = {
    hint: 'Requisições',
    total: quantidadeNotificacoes,
    itens: [{ key: 'notificacao', texto: 'Você tem Requisições aguardando.' }],
  };

  // Mesmos gatilhos do projeto real (Toolbar.jsx: goToAcordosAprovados/goToAutorizacao/goToProcessos),
  // mas o destino agora é a grid do próprio protótipo (ver useDispararPesquisaAtalho).
  const irPara = (tipo, item) => {
    if (tipo === 'balao') {
      lerNotificacaoProcessoAtivoService(item.idProcesso)
        .then(() => dispatch(findQuantidadeAcordosAprovadosAction()))
        .catch(() => {});
      disparar('numeroProcesso', { numeroProcesso: item.idProcesso });
    }
    if (tipo === 'cifrao') disparar('autorizacao');
    if (tipo === 'sino') disparar('notificacao');
  };

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        minHeight: 56,
        px: 'clamp(12px, 1vw, 28px)',
        pt: 1,
      }}
    >
      {/* Chip navy com o nome da tela — nos prints só aparece fora da home. */}
      <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
        {titulo ? (
          <Box
            sx={{
              backgroundColor: C.navy,
              color: C.white,
              borderRadius: `${C.radiusCard}px`,
              px: 'clamp(14px, 1vw, 26px)',
              py: 1.25,
              boxShadow: C.shadowCard,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 'clamp(15px, 0.9vw, 22px)', lineHeight: 1.2 }}>
              {titulo}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(0px, 0.3vw, 8px)' }}>
        <Tooltip title="Home" arrow>
          <IconButton aria-label="Home" onClick={() => history.push(rota('/home'))} sx={{ color: C.navy }}>
            <HomeOutlinedIcon />
          </IconButton>
        </Tooltip>

        <NotificacaoIcon
          tipo="balao"
          dados={dadosBalao}
          icone={<ChatBubbleOutlineIcon />}
          onNavigate={irPara}
        />
        <NotificacaoIcon
          tipo="cifrao"
          dados={dadosCifrao}
          icone={<AttachMoneyIcon />}
          onNavigate={irPara}
        />
        <NotificacaoIcon
          tipo="sino"
          dados={dadosSino}
          icone={<NotificationsNoneIcon />}
          onNavigate={irPara}
        />

        <Tooltip title="Ir para o Layout Antigo" arrow>
          <IconButton
            aria-label="Ir para o Layout Antigo"
            onClick={() => history.push(ROTA_DASHBOARD)}
            sx={{ color: C.navy }}
          >
            <SwapHorizIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Configurações" arrow>
          <IconButton
            aria-label="Configurações"
            onClick={() => dispatch(setOpenDialogAlterarSenhaAction(true))}
            sx={{ color: C.navy }}
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </Tooltip>
        <AlterarSenhaDialog />
        <Tooltip title="Sair" arrow>
          <IconButton
            aria-label="Sair"
            onClick={() => dispatch(logoutAction())}
            sx={{ color: C.navy }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {snackbar}
    </Box>
  );
}
Topbar.propTypes = { titulo: PropTypes.string };
Topbar.defaultProps = { titulo: null };
